import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Otto from './Otto';
import { ottoMotionSuppressed } from '../../lib/motion';
import './otto.css';

// Award-brief celebration — Otto (Celebrating) pops in and throws
// multicolour confetti that launches from his wing-tips, then settles
// and slides away as the "Awarded to …" confirmation shows. Drop-in for
// the old AwardTurboPeek: same { show, onComplete } contract. Serves the
// award moment; the same Celebrating pose also covers Oak Status via its
// own trigger. Reduced-motion: skip entirely, still fire onComplete so
// the caller's state resets.
//
// `airshow` upgrades the big moments (Oak status, award milestones): Otto
// flies a loop-the-loop and a barrel roll before the confetti throw.
const DURATION_MS = 2100;
const AIRSHOW_MS = 1700; // loop + roll before the throw
// Brand-palette confetti only (build brief #9/#10 cross-cutting decision):
// no amber, no purple — lime / deep-green / teal / light-green.
const PALETTE = ['#92D400', '#009159', '#2E8FA1', '#B4E2A5'];

// Was a local copy of the reduced-motion predicate. Now the shared one, which
// ORs in the per-user Otto toggle.
const prefersReducedMotion = ottoMotionSuppressed;

function burst(wrap) {
  const originX = wrap.clientWidth / 2; // centre of Otto
  const originY = 16;                   // near his raised wing-tips
  for (let i = 0; i < 34; i++) {
    const c = document.createElement('div');
    c.className = 'otto-confetti';
    c.style.background = PALETTE[i % PALETTE.length];
    if (i % 3 === 0) c.style.borderRadius = '50%';
    else if (i % 3 === 1) { c.style.width = '6px'; c.style.height = '11px'; }
    c.style.left = `${originX}px`;
    c.style.top = `${originY}px`;
    wrap.appendChild(c);
    const ang = (-100 + Math.random() * 80) * Math.PI / 180; // fan upward
    const vel = 70 + Math.random() * 90;
    const dx = Math.cos(ang) * vel;
    const dy = Math.sin(ang) * vel;
    const rot = Math.random() * 720 - 360;
    c.animate([
      { transform: 'translate(0,0) rotate(0)', opacity: 1 },
      { transform: `translate(${dx * 0.5}px,${dy}px) rotate(${rot * 0.5}deg)`, opacity: 1, offset: 0.4 },
      { transform: `translate(${dx}px,${120 + Math.random() * 40}px) rotate(${rot}deg)`, opacity: 0 },
    ], { duration: 1100 + Math.random() * 500, easing: 'cubic-bezier(.2,.6,.5,1)', fill: 'forwards' });
    setTimeout(() => c.remove(), 1700);
  }
}

export default function OttoAward({ show, onComplete, label, airshow = false }) {
  const [active, setActive] = useState(false);
  // 'fly' = loop/roll airshow in progress; 'throw' = confetti moment.
  const [phase, setPhase] = useState('throw');
  const [nonce, setNonce] = useState(0);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const flyTimerRef = useRef(null);

  useEffect(() => {
    if (!show) return undefined;
    if (prefersReducedMotion()) { onComplete?.(); return undefined; }
    setActive(true);
    setNonce(n => n + 1);
    const doAirshow = airshow;
    setPhase(doAirshow ? 'fly' : 'throw');
    if (doAirshow) {
      flyTimerRef.current = setTimeout(() => setPhase('throw'), AIRSHOW_MS);
    }
    const total = DURATION_MS + (doAirshow ? AIRSHOW_MS : 0);
    timerRef.current = setTimeout(() => { setActive(false); onComplete?.(); }, total);
    return () => { clearTimeout(timerRef.current); clearTimeout(flyTimerRef.current); };
    // Key off `show` only so a re-toggle re-fires a fresh celebration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => () => { clearTimeout(timerRef.current); clearTimeout(flyTimerRef.current); }, []);

  // Throw the confetti from Otto's wings once he settles into the throw
  // (immediately without an airshow, or right after the loop + roll).
  useEffect(() => {
    if (!active || phase !== 'throw' || !wrapRef.current) return undefined;
    const wrap = wrapRef.current;
    const t = setTimeout(() => burst(wrap), 420);
    return () => clearTimeout(t);
  }, [active, phase, nonce]);

  if (!active || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={wrapRef}
      key={nonce}
      aria-hidden="true"
      style={{ position: 'fixed', left: '50%', bottom: '9%', transform: 'translateX(-50%)', zIndex: 120, pointerEvents: 'none' }}>
      {/* Every award is a big moment → Captain Otto (dress uniform).
          A milestone award adds the label flourish on top; airshow does
          a loop + roll before the throw. */}
      <div className={`otto-award otto-in ${phase === 'fly' ? 'otto-airshow' : 'otto-throw'}`}>
        <div className="otto-thrower"><Otto pose="celebrating" variant="captain" size={104} ariaLabel="" /></div>
      </div>
      {label && (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <span style={{
            display: 'inline-block', background: '#14210A', color: '#92D400',
            fontFamily: 'Metropolis, sans-serif', fontWeight: 800, fontSize: 13,
            letterSpacing: '0.02em', padding: '6px 14px', borderRadius: 999,
            boxShadow: '0 8px 20px -8px rgba(0,0,0,0.5)',
          }}>
            {label}
          </span>
        </div>
      )}
    </div>,
    document.body
  );
}
